import React, { useEffect, useState } from "react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./CustomerStats.css";
import { motion } from "framer-motion";
import {
  Users2,
  FileText,
  AlarmClock,
  Clock3,
  CheckCircle2,
} from "lucide-react";

const CustomerStats = () => {
  const { companyId } = useCompany();
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalInvoiced, setTotalInvoiced] = useState(0);
  const [overdueInvoices, setOverdueInvoices] = useState(0);
  const [openInvoices, setOpenInvoices] = useState(0);
  const [recentlyPaid, setRecentlyPaid] = useState(0);

  useEffect(() => {
    if (!companyId) return; // wait until companyId is set

    const fetchStats = async () => {
      // —— Total Customers ——
      const { count: custCount, error: custErr } = await supabase
        .from("customers")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId);

      if (custErr) {
        console.error("Error counting customers:", custErr);
      } else {
        setTotalCustomers(custCount || 0);
      }

      // —— All Invoices for this company ——
      const { data: invData, error: invErr } = await supabase
        .from("invoices")
        .select("total, status")
        .eq("company_id", companyId);

      if (invErr) {
        console.error("Error fetching invoices:", invErr);
        return;
      }

      // Sum up totals by status
      let invoicedSum = 0,
        overdueSum = 0,
        openSum = 0,
        paidSum = 0;

      invData.forEach((inv) => {
        invoicedSum += inv.total;
        if (inv.status === "overdue") overdueSum += inv.total;
        else if (inv.status === "open") openSum += inv.total;
        else if (inv.status === "paid") paidSum += inv.total;
      });

      setTotalInvoiced(invoicedSum);
      setOverdueInvoices(overdueSum);
      setOpenInvoices(openSum);
      setRecentlyPaid(paidSum);
    };

    fetchStats();
  }, [companyId]);

  // Avoid divide-by-zero
  const pct = (part) =>
    totalInvoiced > 0 ? `${Math.round((part / totalInvoiced) * 100)}%` : "0%";

  // Animation presets
  const fadeUp = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { duration: 0.25 } },
  };
  const stagger = { show: { transition: { staggerChildren: 0.06 } } };

  return (
    <section className="customer-stats-container panel">
      <div className="stats-head">
        <h2 id="stats-h2">Customers</h2>
        <div className="stats-sub">
          <span className="chip soft">
            <Users2 size={14} /> {totalCustomers} total
          </span>
          <span className="chip prime">
            <FileText size={14} /> Invoiced: ₦{totalInvoiced.toLocaleString()}
          </span>
        </div>
      </div>

      {/* KPI strip */}
      <motion.div
        className="stats-grid"
        variants={stagger}
        initial="hidden"
        animate="show"
      >
        <motion.div className="stat-item glass" variants={fadeUp}>
          <div className="stat-icon">
            <Users2 size={18} />
          </div>
          <div className="stat-copy">
            <h3 id="stat1-h3">{totalCustomers.toLocaleString()}</h3>
            <p>Total Customers</p>
          </div>
        </motion.div>

        <motion.div className="stat-item glass" variants={fadeUp}>
          <div className="stat-icon">
            <FileText size={18} />
          </div>
          <div className="stat-copy">
            <h3 id="stat1-h3">₦{totalInvoiced.toLocaleString()}</h3>
            <p>Total Invoiced</p>
          </div>
        </motion.div>

        <motion.div className="stat-item glass" variants={fadeUp}>
          <div className="stat-icon overdue">
            <AlarmClock size={18} />
          </div>
          <div className="stat-copy">
            <h3 id="stat1-h3">₦{overdueInvoices.toLocaleString()}</h3>
            <p>Overdue Invoices</p>
          </div>
        </motion.div>

        <motion.div className="stat-item glass" variants={fadeUp}>
          <div className="stat-icon open">
            <Clock3 size={18} />
          </div>
          <div className="stat-copy">
            <h3 id="stat1-h3">₦{openInvoices.toLocaleString()}</h3>
            <p>Open Invoices</p>
          </div>
        </motion.div>

        <motion.div className="stat-item glass" variants={fadeUp}>
          <div className="stat-icon paid">
            <CheckCircle2 size={18} />
          </div>
          <div className="stat-copy">
            <h3 id="stat1-h3">₦{recentlyPaid.toLocaleString()}</h3>
            <p>Recently Paid</p>
          </div>
        </motion.div>
      </motion.div>

      {/* Stacked progress bar */}
      <div className="progress-block">
        <div className="progress-bar" aria-label="Invoice status distribution">
          <div
            className="bar overdue"
            style={{ width: pct(overdueInvoices) }}
            title={`Overdue • ${pct(overdueInvoices)}`}
          />
          <div
            className="bar open"
            style={{ width: pct(openInvoices) }}
            title={`Open • ${pct(openInvoices)}`}
          />
          <div
            className="bar paid"
            style={{ width: pct(recentlyPaid) }}
            title={`Paid • ${pct(recentlyPaid)}`}
          />
        </div>

        <ul className="progress-legend">
          <li>
            <span className="dot overdue" /> Overdue ({pct(overdueInvoices)})
          </li>
          <li>
            <span className="dot open" /> Open ({pct(openInvoices)})
          </li>
          <li>
            <span className="dot paid" /> Paid ({pct(recentlyPaid)})
          </li>
        </ul>
      </div>
    </section>
  );
};

export default CustomerStats;
